import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { AchievementNotification } from '@/components/achievement-notification';

interface Achievement {
  id: number;
  name: string;
  description: string;
  icon: string;
  rarity_label: string;
}

interface AchievementContextType {
  showAchievement: (achievement: Achievement) => void;
  clearAchievements: () => void;
}

const AchievementContext = createContext<AchievementContextType | undefined>(undefined);

interface AchievementProviderProps {
  children: ReactNode;
}

export function AchievementProvider({ children }: AchievementProviderProps) {
  const [achievements, setAchievements] = useState<Achievement[]>([]);

  // Memoize callbacks to prevent re-renders
  const showAchievement = useCallback((achievement: Achievement) => {
    setAchievements(prev => [...prev, achievement]);
  }, []);

  const removeAchievement = useCallback((achievementId: number) => {
    setAchievements(prev => prev.filter(achievement => achievement.id !== achievementId));
  }, []);

  const clearAchievements = useCallback(() => {
    setAchievements([]);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value: AchievementContextType = useMemo(() => ({
    showAchievement,
    clearAchievements,
  }), [showAchievement, clearAchievements]);

  return (
    <AchievementContext.Provider value={value}>
      {children}
      
      {/* Achievement Notifications */}
      {Array.isArray(achievements) && achievements.map((achievement, index) => (
        <div key={`${achievement.id}-${index}`} style={{ top: `${4 + (index * 5)}rem` }}>
          <AchievementNotification
            achievement={achievement}
            onClose={() => removeAchievement(achievement.id)}
          />
        </div>
      ))}
    </AchievementContext.Provider>
  );
}

export function useAchievementContext() {
  const context = useContext(AchievementContext);
  if (context === undefined) {
    throw new Error('useAchievementContext must be used within an AchievementProvider');
  }
  return context;
} 